import React, { useCallback, useEffect, useState } from "react";
import { Input, Spin } from "antd";
import { FileTextOutlined, LockOutlined } from "@ant-design/icons";
import { NotificationManager } from "react-notifications";
import moment from "moment";
import KycService from "../../services/kyc-service";
import { KYC_DEFAULT_FILTER_PAYLOAD } from "./kycDesignShared";
import { getKycAppliedFilterPayload, KYC_CREATED_ON_FORMATS } from "./kycHelpers";

const normalizeFieldKey = (key) =>
	String(key || "")
		.replace(/^ispl_/i, "")
		.replace(/[_-]/g, "")
		.toLowerCase();

const getCreditFieldValue = (record, keys = []) => {
	if (!record || typeof record !== "object") {
		return "";
	}

	for (const key of keys) {
		const value = record[key];
		if (value !== undefined && value !== null && String(value).trim() !== "") {
			return value;
		}
	}

	const normalizedKeys = keys.map(normalizeFieldKey);
	for (const [key, value] of Object.entries(record)) {
		if (value === undefined || value === null || String(value).trim() === "") {
			continue;
		}
		if (normalizedKeys.includes(normalizeFieldKey(key))) {
			return value;
		}
	}

	return "";
};

const parseNumericValue = (value) => {
	if (value === undefined || value === null || value === "") {
		return null;
	}
	const numeric = Number(String(value).replace(/,/g, "").trim());
	return Number.isNaN(numeric) ? null : numeric;
};

const formatCreditAmount = (value) => {
	const numeric = parseNumericValue(value);
	if (numeric === null) {
		return "";
	}
	return numeric.toLocaleString("en-IN", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

const formatCreditDateTime = (value) => {
	if (value === undefined || value === null || value === "") {
		return "";
	}
	const parsedDate = moment(value, [...KYC_CREATED_ON_FORMATS, "DD-MM-YYYY HH:mm", "DD/MM/YYYY HH:mm"], true);
	if (parsedDate.isValid()) {
		return parsedDate.format("DD-MM-YYYY HH:mm");
	}
	const fallbackDate = moment(value);
	return fallbackDate.isValid() ? fallbackDate.format("DD-MM-YYYY HH:mm") : String(value).trim();
};

const CREDIT_FIELDS = {
	creditLimitInSap: ["creditLimitInSAP", "creditLimitInSap", "creditLimitSAP", "sapCreditLimit", "creditLimit"],
	openOrder: ["openorder", "openOrder", "openOrders", "open_Order"],
	openInvoice: ["openinvoice", "openInvoice", "plusOpenInvoice", "open_Invoice"],
	creditExposureInSap: [
		"creditExposureInSAP",
		"creditExposureInSap",
		"creditExposureSAP",
		"creditExposure",
	],
	crUtilisedInSap: [
		"sapopenOrders",
		"crUtilisedOfThisOrderInSAP",
		"crUtilisedOfThisOrderInSap",
		"creditUtilisedOfThisOrderInSAP",
		"crUtilised",
		"creditUtilised",
	],
	overdueDays: ["overduedays", "overdueDays", "overDueDays", "overdue", "overDue"],
	creditLimitCrm: ["creditlimitCRM", "creditLimitCRM", "creditLimitCrm", "creditLimit_CRM"],
	creditLimitCrmBase: [
		"creditlimitCRMbase",
		"creditLimitCRMBase",
		"creditLimitCrmBase",
		"creditLimitBase",
		"creditLimitCRM_Base",
	],
	tax: ["tax", "taxPercent", "taxPercentage"],
	currentOrder: ["currentOrders", "currentOrder", "current_Order"],
	lastUpdated: ["lastUpdatedOn", "lastUpdated", "modifiedOn", "modifiedAt", "updatedOn"],
	taxAmount: ["totalAmountInctax", "taxAmount", "tax_Amount"],
};

const parseCreditDetailsResponse = (response) => {
	const success = response?.data?.statusCode === 200 || response?.data?.status === true;
	if (!success) {
		return null;
	}
	const responseData = response.data.responseData || {};
	const creditList = Array.isArray(responseData)
		? responseData
		: responseData.creditDetailsLists ||
			responseData.creditDetailsList ||
			responseData.kycCreditDetailsLists ||
			responseData.creditDetails ||
			responseData.data ||
			[];
	if (Array.isArray(creditList)) {
		return creditList[0] || null;
	}
	return creditList && typeof creditList === "object" ? creditList : null;
};

const CreditLockedInput = ({ value, prefixIcon, currency }) => (
	<Input
		readOnly
		className="kyc-credit-details-input"
		prefix={prefixIcon}
		value={value ? `${currency ? `${currency} ` : ""}${value}` : ""}
		placeholder="-"
	/>
);

const CreditFieldRow = ({ label, value, icon = <LockOutlined />, currency = "", stacked = false }) => (
	<div className={`kyc-credit-details__row${stacked ? " is-stacked" : ""}`}>
		<div className="kyc-credit-details__label">{label}</div>
		<div className="kyc-credit-details__control">
			<CreditLockedInput value={value} prefixIcon={icon} currency={currency} />
		</div>
	</div>
);

const KycPartnerCreditDetailTab = ({
	customer = "",
	isActive,
	navigationContext = {},
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [creditDetails, setCreditDetails] = useState(null);

	const loadCreditDetails = useCallback(async () => {
		const trimmedCustomer = String(customer || "").trim();
		if (!trimmedCustomer) {
			setCreditDetails(null);
			return;
		}

		setIsLoading(true);
		try {
			const requestData = {
				pageindex: 1,
				pagesize: 10,
				sortcolumn: "",
				orderBy: "",
				search: "",
				...KYC_DEFAULT_FILTER_PAYLOAD,
				...getKycAppliedFilterPayload(),
				ispl_product: "",
				customer: trimmedCustomer,
			};
			const groupCompanyId = String(navigationContext.groupCompanyId || "").trim();
			if (groupCompanyId) {
				requestData.groupcomanyId = groupCompanyId;
			}

			const response = await KycService.getKycCreditDetailsListAPI(requestData);
			const record = parseCreditDetailsResponse(response);
			setCreditDetails(record);
			if (!record && response?.data?.statusCode !== 200 && response?.data?.status !== true) {
				NotificationManager.error(response?.data?.message || "Unable to load credit details.");
			}
		} catch (error) {
			console.log(error, "Error");
			NotificationManager.error("Unable to load credit details.");
			setCreditDetails(null);
		} finally {
			setIsLoading(false);
		}
	}, [customer, navigationContext.groupCompanyId]);

	useEffect(() => {
		if (!isActive) {
			return undefined;
		}
		loadCreditDetails();
		return undefined;
	}, [isActive, loadCreditDetails]);

	const getFormattedAmount = (keys) => formatCreditAmount(getCreditFieldValue(creditDetails, keys));
	const lastUpdatedValue = formatCreditDateTime(getCreditFieldValue(creditDetails, CREDIT_FIELDS.lastUpdated));

	return (
		<Spin spinning={isLoading}>
			<div className="kyc-credit-details">
				<div className="kyc-credit-details__panel">
					<h5 className="kyc-credit-details__title">SAP Details</h5>
					<CreditFieldRow label="Credit Limit in SAP" value={getFormattedAmount(CREDIT_FIELDS.creditLimitInSap)} />
					<CreditFieldRow label="Open Order" value={getFormattedAmount(CREDIT_FIELDS.openOrder)} />
					<CreditFieldRow label="+Open Invoice" value={getFormattedAmount(CREDIT_FIELDS.openInvoice)} />
					<CreditFieldRow
						label="=Credit Exposure in SAP"
						value={getFormattedAmount(CREDIT_FIELDS.creditExposureInSap)}
					/>
					<CreditFieldRow
						label={
							<>
								-Cr. Utilised of this Order
								<br />
								in SAP
							</>
						}
						value={getFormattedAmount(CREDIT_FIELDS.crUtilisedInSap)}
						stacked
					/>
					<CreditFieldRow label="Overdue days" value={getFormattedAmount(CREDIT_FIELDS.overdueDays)} />
				</div>

				<div className="kyc-credit-details__panel">
					<div className="kyc-credit-details__nested">
						<h5 className="kyc-credit-details__title">CRM Details-In Base Currency</h5>
						<CreditFieldRow
							label="Credit Limit-CRM"
							value={getFormattedAmount(CREDIT_FIELDS.creditLimitCrm)}
						/>
						<CreditFieldRow
							label="Credit Limit -CRM (Base)"
							value={getFormattedAmount(CREDIT_FIELDS.creditLimitCrmBase)}
						/>
					</div>
					<CreditFieldRow label="Tax" value={getFormattedAmount(CREDIT_FIELDS.tax)} />
					<CreditFieldRow
						label="Current Order"
						value={getFormattedAmount(CREDIT_FIELDS.currentOrder)}
						icon={<FileTextOutlined />}
					/>
					<CreditFieldRow
						label="Tax Amount"
						value={getFormattedAmount(CREDIT_FIELDS.taxAmount)}
					/>
				</div>
			</div>
		</Spin>
	);
};

export default KycPartnerCreditDetailTab;
